import { Sparkles, Loader2 } from 'lucide-react'
import { useAIAction } from '../../hooks/useAI.jsx'
import { useTranslation } from 'react-i18next'

const REC_STYLES = {
  approve: 'bg-green-50 border-green-200 text-green-800',
  request_revision: 'bg-amber-50 border-amber-200 text-amber-800',
  reject: 'bg-red-50 border-red-200 text-red-800',
}
const REC_LABELS = {
  approve: '✓ Recommend: Approve',
  request_revision: '🔄 Recommend: Request Revision',
  reject: '✗ Recommend: Reject',
}

/**
 * AIDeliverableCheck — button + result panel inside the PM review area.
 * Reads the uploaded file, compares it to the milestone brief, and gives
 * the PM an advisory summary + recommendation. PM still decides.
 */
export default function AIDeliverableCheck({ deliverableId }) {
  const { t } = useTranslation('assistant')
  const { data, loading, error, run } = useAIAction()

  if (!data) {
    return (
      <div>
        <button
          onClick={() => run(`/ai/deliverable-check/${deliverableId}`)}
          disabled={loading}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-indigo-700 border border-indigo-200 bg-indigo-50 rounded hover:bg-indigo-100 disabled:opacity-60"
        >
          {loading
            ? <><Loader2 className="w-3 h-3 animate-spin" /> Reading the file…</>
            : <><Sparkles className="w-3 h-3" /> AI Quality Check</>}
        </button>
        {error && <p className="text-[11px] text-red-500 mt-1">{error}</p>}
      </div>
    )
  }

  if (data.error || data.available === false) {
    return <p className="text-[11px] text-gray-400">AI check unavailable: {data.error || 'no API key configured'}</p>
  }

  return (
    <div className="bg-indigo-50/50 border border-indigo-100 rounded-lg p-3 space-y-2 text-xs">
      <div className="flex items-center gap-1.5 text-indigo-700 font-semibold">
        <Sparkles className="w-3 h-3" /> AI Quality Check
        <span className={`ml-auto px-2 py-0.5 rounded-full border font-semibold ${REC_STYLES[data.recommendation] || 'bg-gray-50 border-gray-200 text-gray-600'}`}>
          {REC_LABELS[data.recommendation] || data.recommendation}
        </span>
      </div>
      <p className="text-gray-700">{data.summary}</p>
      {data.covers_brief && (
        <p className="text-gray-500">{t('covers_brief')} <span className="font-medium text-gray-700">{data.covers_brief}</span></p>
      )}
      {data.strengths?.length > 0 && (
        <ul className="space-y-0.5">
          {data.strengths.slice(0, 3).map((s, i) => <li key={i} className="text-green-700">+ {s}</li>)}
        </ul>
      )}
      {data.concerns?.length > 0 && (
        <ul className="space-y-0.5">
          {data.concerns.slice(0, 3).map((c, i) => <li key={i} className="text-amber-700">! {c}</li>)}
        </ul>
      )}
      {data.recommendation_reason && (
        <p className="text-gray-500 italic border-t border-indigo-100 pt-1.5">{data.recommendation_reason}</p>
      )}
      <p className="text-[10px] text-gray-400">{t('advisory_only')}</p>
    </div>
  )
}
